import React, { useCallback } from 'react';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { PagePhoto as PagePhotoIcon, Photos } from '@withjoy/joykit/icons';
import { Flex, ButtonV2, TextV2, useDisclosure, Box } from '@withjoy/joykit';
import { TempSpinner } from '@shared/components/TempSpinner';
import { PhotoMenu, PhotoMenuProps } from '@shared/components/PhotoMenu';
import { PagePhotoSettings } from '@shared/components/FocusPointEditor/PagePhotoSettings';
import { EventPageType, PhotoDataFragment, WebsitePagePhotoPresentationFragment } from '@graphql/generated';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { ImageData } from '@shared/components/FocusPointEditor/FocusPointEditor.types';
import { useFocusPointEditorTelemetry } from '@shared/components/FocusPointEditor/FocusPointEditor.telemetry';

export interface PagePhotoProps
  extends Readonly<Omit<PhotoMenuProps, 'photoExists' | 'children'>>,
    Readonly<{
      photo?: string;
      loading?: boolean;
      label?: string;
      imageData?: ImageData | null;
      pageType?: EventPageType;
      pageId?: string;
      updatePagePhotoPreview?: (filename: string, imageInfo: PhotoDataFragment, photoPresentation?: Maybe<WebsitePagePhotoPresentationFragment>) => void;
      onPhotoDeleteSuccess?: () => void;
      renderPhotoFocus?: boolean;
      allowPagePhoto?: boolean;
      pageSlug?: string;
      photoPresentation?: Maybe<WebsitePagePhotoPresentationFragment>;
      enablePhotoLayout?: boolean;
      photoSize?: { width: number; height: number };
    }> {}

const PagePhoto: React.FC<PagePhotoProps> = props => {
  const {
    photo,
    loading,
    label,
    handleUpdatePhoto,
    handleDeletePhoto,
    handleResetPhoto,
    hasDefaultPhoto,
    imageData,
    pageId,
    pageType,
    updatePagePhotoPreview,
    onPhotoDeleteSuccess,
    renderPhotoFocus = false,
    allowPagePhoto = true,
    pageSlug,
    photoPresentation = null,
    enablePhotoLayout = false,
    photoSize = { width: 56, height: 56 },
  } = props;

  const { isOpen, onOpen, onClose } = useDisclosure();

  const focusPointEditorTelemetry = useFocusPointEditorTelemetry();

  const { tq } = useTranslation('websiteEditor');

  const handleFileStackClose = useCallback(
    (hasImageUploaded: boolean) => {
      if (!hasImageUploaded) {
        onClose();
      }
    },
    [onClose],
  );

  const handleFocusPointEditorOpen = useCallback(() => {
    if (!allowPagePhoto) return;
    focusPointEditorTelemetry.photoEditorChangePhotoClicked('website');
    onOpen();
  }, [allowPagePhoto, focusPointEditorTelemetry, onOpen]);

  const renderPhoto = useCallback(
    () => (
      <ButtonV2
        variant="ghost"
        as="div"
        padding={0}
        height="100%"
        color="mono14"
        intent="neutral"
        textDecoration="none"
        fontWeight={600}
        display="flex"
        columnGap={4}
        alignItems="center"
        role="group"
        disabled={!allowPagePhoto}
      >
        <Flex
          width={pxToRem(photoSize.width)}
          height={pxToRem(photoSize.height)}
          borderRadius={2}
          backgroundColor="mono2"
          position="relative"
          backgroundImage={photo ? `url('${photo}')` : ''}
          backgroundPosition="center"
          backgroundSize="cover"
          _groupHover={{
            background: photo ? `linear-gradient(0deg, rgba(255, 255, 255, 0.20) 0%, rgba(255, 255, 255, 0.20) 100%), url("${photo}") lightgray 50%` : '#FBFBFB',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
          _groupActive={{
            background: photo ? `linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), url("${photo}") lightgray 50%` : '#EBEBEB',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
          alignItems="center"
          justifyContent="center"
          backgroundRepeat="no-repeat"
          __css={{ ['.loading > div']: { width: pxToRem(20), height: pxToRem(20) } }}
        >
          {loading && (
            <TempSpinner
              position={'absolute'}
              className="loading"
            />
          )}
          {!photo &&
            !loading &&
            (renderPhotoFocus ? (
              <PagePhotoIcon
                color="mono5"
                size={32}
              />
            ) : (
              <Photos
                color="mono5"
                size={32}
              />
            ))}
        </Flex>
        <TextV2 typographyVariant="button2">{label ?? tq`Page Photo`}</TextV2>
      </ButtonV2>
    ),
    [allowPagePhoto, photoSize.width, photoSize.height, photo, loading, renderPhotoFocus, label, tq],
  );

  return (
    <>
      {!renderPhotoFocus ? (
        <PhotoMenu
          handleUpdatePhoto={handleUpdatePhoto}
          handleDeletePhoto={handleDeletePhoto}
          handleResetPhoto={handleResetPhoto}
          photoExists={!!photo}
          hasDefaultPhoto={hasDefaultPhoto}
        >
          {renderPhoto()}
        </PhotoMenu>
      ) : (
        <>
          <Box
            onClick={handleFocusPointEditorOpen}
            cursor={allowPagePhoto ? 'pointer' : 'none'}
          >
            {renderPhoto()}
          </Box>
          <ResponsiveDialog
            isOpen={isOpen}
            onClose={onClose}
            hideDivider={true}
            removeBodyPadding
            hasCloseButton={false}
            drawerOverrides={{
              ...drawerOverrides,
              Content: {
                props: {
                  ...drawerOverrides.Content?.props,
                  boxShadow: '0px -7px 16px -8px rgba(0, 0, 0, 0.3), 0px -10px 27px -5px rgba(44, 41, 37, 0.25)',
                },
              },
              Body: { props: { ...drawerOverrides.Body?.props, paddingY: 8 } },
            }}
            dialogOverrides={{
              Content: { props: { width: ['100%', '480px'] } },
              Body: { props: { paddingX: 8, paddingY: 8 } },
            }}
            disableFocusLock={true}
          >
            <PagePhotoSettings
              isOpen={isOpen}
              handleOnClose={onClose}
              handleOnCancel={onClose}
              imageData={imageData}
              pageType={pageType}
              pageId={pageId}
              showNavigateBack={false}
              updatePagePhotoPreview={updatePagePhotoPreview}
              onPhotoDeleteSuccess={onPhotoDeleteSuccess}
              onFileStackClose={handleFileStackClose}
              telemetryCategory="website"
              allowPagePhoto={allowPagePhoto}
              pageSlug={pageSlug}
              photoPresentation={photoPresentation}
              enablePhotoLayout={enablePhotoLayout}
            />
          </ResponsiveDialog>
        </>
      )}
    </>
  );
};

export { PagePhoto };
